import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import type {
  Conversation,
  Message,
} from "@agentic_conversation/pages/chat/types";

export type { Conversation, Message };

const BASE_URL = "/system/agentic_conversation/conversations";

export const conversationApi = {
  list: async (): Promise<Conversation[]> => {
    const { data } = await axios.get(`${BASE_URL}.json`);
    return data;
  },

  get: async (id: number): Promise<Conversation> => {
    const { data } = await axios.get(`${BASE_URL}/${id}.json`);
    return data;
  },

  create: async (label?: string): Promise<Conversation> => {
    const { data } = await axios.post(`${BASE_URL}.json`, {
      conversation: { label },
    });
    return data;
  },

  ask: async (id: number, question: string): Promise<Message> => {
    const { data } = await axios.post(`${BASE_URL}/${id}/ask.json`, {
      question,
    });
    return data;
  },

  askAgent: async (id: number, agentId: number): Promise<Message> => {
    const { data } = await axios.post(`${BASE_URL}/${id}/ask_agent.json`, {
      agent_id: agentId,
    });
    return data;
  },

  retry: async (id: number): Promise<{ message: string; user_message_id: number }> => {
    const { data } = await axios.post(`${BASE_URL}/${id}/retry.json`);
    return data;
  },

  quotaStatus: async (): Promise<QuotaStatus> => {
    const { data } = await axios.get("/system/agentic_conversation/quota_status.json");
    return data;
  },
};

export interface QuotaStatus {
  sent_count: number;
  limit: number | null;
  exceeded: boolean;
}

export const conversationKeys = {
  all: ["conversations"] as const,
  detail: (id: number) => ["conversations", id] as const,
  quotaStatus: ["quota_status"] as const,
};

export function useConversations() {
  return useQuery({
    queryKey: conversationKeys.all,
    queryFn: conversationApi.list,
  });
}

export function useConversation(id: number | null) {
  return useQuery({
    queryKey: conversationKeys.detail(id!),
    queryFn: () => conversationApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: conversationApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      toast.success("Conversation créée");
    },
    onError: (error: Error) => {
      toast.error("Impossible de créer la conversation");
      console.error("Create conversation error:", error);
    },
  });
}

export function useAskQuestion(conversationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (question: string) =>
      conversationApi.ask(conversationId, question),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
    },
    onError: (error: Error) => {
      toast.error("Impossible d'envoyer le message");
      console.error("Ask question error:", error);
    },
  });
}

export function useAskAgent(conversationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: number) =>
      conversationApi.askAgent(conversationId, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
    },
    onError: (error: Error) => {
      toast.error("Impossible d'envoyer la question à l'agent");
      console.error("Ask agent error:", error);
    },
  });
}

export function useQuotaStatus() {
  return useQuery({
    queryKey: conversationKeys.quotaStatus,
    queryFn: conversationApi.quotaStatus,
  });
}

export function useRetryConversation(conversationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => conversationApi.retry(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId),
      });
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      // Don't show toast for token_limit_exceeded - component handles this
      if (error.response?.data?.error !== "token_limit_exceeded") {
        toast.error("Impossible de redémarrer la conversation");
      }
      console.error("Retry conversation error:", error);
    },
  });
}
